import { FormField } from "@/overlay/components/form-field";
import { classNames } from "@/utility/jsx";
import { t } from "@lingui/core/macro";
import { enumValues } from "@mm/api.meteomatics.com";
import { type MapProjection, ProjectionNameEnum } from "@mm/metx-workbench.meteomatics.com";
import { clamp, debounce } from "lodash";
import { useMemo, useState } from "react";
import { useCallback } from "react";
import styles from "../StyleOptionsPanel/style.module.scss";

export const projectionToFilterOut: ProjectionNameEnum[] = [
  ProjectionNameEnum.albers,
  ProjectionNameEnum.equalEarth,
  ProjectionNameEnum.equirectangular,
  ProjectionNameEnum.naturalEarth,
  ProjectionNameEnum.winkelTripel,
];
export const projectionNames = enumValues(ProjectionNameEnum);

interface ProjectionProps {
  mapProjection: MapProjection;
  onProjectionUpdate: (fiels: MapProjection) => void;
}

export function MapProjectionPanel({ mapProjection, onProjectionUpdate }: ProjectionProps) {
  const [expanded, setExpanded] = useState(false);
  const [centerLon, setCenterLon] = useState(String(mapProjection.center ? mapProjection.center[0] : 0));
  const [centerLat, setCenterLat] = useState(String(mapProjection.center ? mapProjection.center[1] : 0));
  const [southernParallelLat, setSouthernParallelLat] = useState(
    String(mapProjection.parallels ? mapProjection.parallels[0] : 0),
  );
  const [northernParallelLat, setNorthernParallelLat] = useState(
    String(mapProjection.parallels ? mapProjection.parallels[1] : 0),
  );

  const safelyParseAndClampValue = (value: string, min: number, max: number) => {
    const parsedValue = Number(value);
    return Number.isNaN(parsedValue) ? 0 : clamp(parsedValue, min, max);
  };

  const setMapProjectionParam = useCallback(
    (value: string, min: number, max: number, type: string) => {
      const newValue: number = safelyParseAndClampValue(value, min, max);

      switch (type) {
        case "centerLon":
          onProjectionUpdate({
            ...mapProjection,
            center: [newValue, mapProjection.center?.[1] || 0],
          });
          setCenterLon(String(newValue));
          break;
        case "centerLat":
          onProjectionUpdate({
            ...mapProjection,
            center: [mapProjection.center?.[0] || 0, newValue],
          });
          setCenterLat(String(newValue));
          break;
        case "southernParallelLat":
          onProjectionUpdate({
            ...mapProjection,
            parallels: [newValue, mapProjection.parallels?.[1] || 0],
          });
          setSouthernParallelLat(String(newValue));
          break;
        case "northernParallelLat":
          onProjectionUpdate({
            ...mapProjection,
            parallels: [mapProjection.parallels?.[0] || 0, newValue],
          });
          setNorthernParallelLat(String(newValue));
          break;
      }
    },
    [mapProjection, onProjectionUpdate],
  );

  const handleProjectionInput = useMemo(() => debounce(setMapProjectionParam, 700), [setMapProjectionParam]);

  function toggleMenu() {
    setExpanded(!expanded);
  }

  function setCenterAndParallelByProjectionName(projectionName: ProjectionNameEnum): Partial<MapProjection> {
    const name = projectionName;
    if (projectionName === ProjectionNameEnum.albers) {
      return {
        name,
        center: [0, 0],
        parallels: [50, 20],
      };
    }
    if (projectionName === ProjectionNameEnum.lambertConformalConic) {
      return {
        name,
        center: [0, 30],
        parallels: [30, 30],
      };
    }
    // reset center and parallels
    return {
      name,
      center: undefined,
      parallels: undefined,
    };
  }

  return (
    <div className={styles["plot-title-style-panel"]}>
      <button className={styles["expandable-panel-header"]} onClick={toggleMenu} type="button">
        <span className="material-icons">{expanded ? "expand_less" : "expand_more"}</span>
        <span className={classNames(styles.label, styles.beta)}>
          <span className="tag secondary">{t`Beta`}</span>
          <span className={styles.tag}>{t`Map Projection`}</span>
          <span className={styles.title}>{mapProjection.name}</span>
        </span>
      </button>
      {expanded && (
        <div className={styles["expandable-panel"]}>
          <FormField label={t`Projection`} removePadding={true}>
            <select
              value={mapProjection.name}
              onChange={(event) =>
                onProjectionUpdate({
                  ...mapProjection,
                  ...setCenterAndParallelByProjectionName(event.target.value as unknown as ProjectionNameEnum),
                })
              }
            >
              {projectionNames
                ?.filter((name) => !projectionToFilterOut.includes(name))
                .map((name, idx) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
            </select>
          </FormField>
          {mapProjection.center && (
            <>
              <FormField removePadding={true} label={t`Center Longitude`}>
                <div className="slide-and-number-input">
                  <input
                    type="range"
                    min="-180"
                    max="180"
                    value={mapProjection.center?.[0]}
                    onChange={(event) => {
                      onProjectionUpdate({
                        ...mapProjection,
                        center: [Number(event.target.value), mapProjection.center?.[1] || 0],
                      });
                      setCenterLon(event.target.value);
                    }}
                  />
                  <input
                    type="text"
                    value={centerLon}
                    onChange={(event) => {
                      setCenterLon(event.target.value);
                      handleProjectionInput(event.target.value, -180, 180, "centerLon");
                    }}
                  />
                </div>
              </FormField>
              <FormField removePadding={true} label={t`Center Latitude`}>
                <div className="slide-and-number-input">
                  <input
                    type="range"
                    min="-90"
                    max="90"
                    value={mapProjection.center?.[1]}
                    onChange={(event) => {
                      onProjectionUpdate({
                        ...mapProjection,
                        center: [mapProjection.center?.[0] || 0, Number(event.target.value)],
                      });
                      setCenterLat(event.target.value);
                    }}
                  />
                  <input
                    type="text"
                    value={centerLat}
                    onChange={(event) => {
                      setCenterLat(event.target.value);
                      handleProjectionInput(event.target.value, -90, 90, "centerLat");
                    }}
                  />
                </div>
              </FormField>
            </>
          )}
          {mapProjection.parallels && (
            <>
              <FormField removePadding={true} label={t`Southern Parallel Lat`}>
                <div className="slide-and-number-input">
                  <input
                    type="range"
                    min="-90"
                    max="90"
                    value={mapProjection.parallels?.[0]}
                    onChange={(event) => {
                      onProjectionUpdate({
                        ...mapProjection,
                        parallels: [Number(event.target.value), mapProjection.parallels?.[1] || 0],
                      });
                      setSouthernParallelLat(event.target.value);
                    }}
                  />
                  <input
                    type="text"
                    value={southernParallelLat}
                    onChange={(event) => {
                      setSouthernParallelLat(event.target.value);
                      handleProjectionInput(event.target.value, -90, 90, "southernParallelLat");
                    }}
                  />
                </div>
              </FormField>
              <FormField removePadding={true} label={t`Northern Parallel Lat`}>
                <div className="slide-and-number-input">
                  <input
                    type="range"
                    min="-90"
                    max="90"
                    value={mapProjection.parallels?.[1]}
                    onChange={(event) => {
                      onProjectionUpdate({
                        ...mapProjection,
                        parallels: [mapProjection.parallels?.[0] || 0, Number(event.target.value)],
                      });
                      setNorthernParallelLat(event.target.value);
                    }}
                  />
                  <input
                    type="text"
                    value={northernParallelLat}
                    onChange={(event) => {
                      setNorthernParallelLat(event.target.value);
                      handleProjectionInput(event.target.value, -90, 90, "northernParallelLat");
                    }}
                  />
                </div>
              </FormField>
            </>
          )}
        </div>
      )}
    </div>
  );
}
